import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Icon } from 'components/Icon';
import { Input, InputIconWrapper } from 'components/Input';
import { H1, Text } from 'components/Typography';
import { lineHeight, theme, toRem } from 'design';
import { useOnClickOutside } from 'hooks';
import { IEmployerSite } from 'types';
import { SiteSelectorAppearance } from './types';

/** Above this many options the menu gets a search input. */
const SEARCH_THRESHOLD = 3;

/** Keys the open menu owns; everything else belongs to the search input. */
const NAV_KEYS = ['ArrowDown', 'ArrowUp', 'Enter', 'Escape'];

const LISTBOX_ID = 'site-select-listbox';

const optionId = (index: number) => `site-option-${index}`;
const buildAddress = (site?: IEmployerSite) => (site?.address ? `${site.address.address1}, ${site.address.city}, ${site.address.state}, ${site.address.zipCode}` : '');

interface ISiteSelectorProps {
  options: IEmployerSite[];
  currentSite?: IEmployerSite;
  onChange: (selected?: IEmployerSite) => void;
  appearance?: SiteSelectorAppearance;
  allSitesEnabled?: boolean;
  allSitesLabel?: string;
  searchPlaceholder?: string;
}

const Layout = styled.div<{ $appearance: SiteSelectorAppearance }>`
  position: relative;
  ${({ $appearance }) =>
    $appearance === SiteSelectorAppearance.DEFAULT &&
    css`
      width: 100%;
      background-color: ${theme.color.white};
    `}
`;

const DropDownSelector = styled.div<{ $isOpen: boolean; $appearance: SiteSelectorAppearance }>`
  display: grid;
  grid-template-columns: auto ${toRem(24)};
  align-items: center;
  grid-gap: ${toRem(theme.space.sm)};
  cursor: pointer;

  &:focus-visible {
    outline: 1px dashed ${theme.color.neutral[800]};
  }

  ${({ $appearance }) =>
    $appearance === SiteSelectorAppearance.DEFAULT
      ? css`
          border-radius: ${toRem(4)};
          border: ${toRem(1)} solid ${theme.color.neutral[200]};
          padding: ${toRem(theme.space.md)} ${toRem(theme.space.lg)};
          width: 100%;
        `
      : css`
          grid-template-columns: auto 1fr;
        `}

  h1 {
    font-size: ${toRem(theme.font.size.md)};
    line-height: ${lineHeight(theme.font.size.md)};
    font-weight: ${theme.font.weight.bold};
    color: ${theme.color.neutral[900]};
    margin: 0;
    text-align: left;

    ${({ $appearance }) =>
      $appearance === SiteSelectorAppearance.PAGE_HEADER &&
      css`
        font-size: clamp(${toRem(theme.font.size['2xl'])}, 5vw, ${toRem(theme.font.size['6xl'])});
        font-weight: ${theme.font.weight.light};
        color: ${theme.color.white};
      `}

    ${({ $appearance }) =>
      $appearance === SiteSelectorAppearance.SECONDARY_HEADER &&
      css`
        font-size: clamp(${toRem(theme.font.size['2xl'])}, 5vw, ${toRem(theme.font.size['3xl'])});
        font-weight: ${theme.font.weight.regular};
        color: ${theme.color.neutral[900]};
      `}

    @media (width < ${theme.breakpoint.small}px) {
      ${({ $appearance }) =>
        $appearance === SiteSelectorAppearance.PAGE_HEADER &&
        css`
          text-align: center;
        `}
    }
  }

  svg {
    height: ${toRem(16)} !important;
    width: ${toRem(16)} !important;
    margin-left: ${toRem(theme.space.sm)};
    fill: ${theme.color.neutral[500]};

    ${({ $appearance }) =>
      $appearance === SiteSelectorAppearance.PAGE_HEADER &&
      css`
        width: ${toRem(24)} !important;
        height: ${toRem(24)} !important;
        fill: ${theme.color.white};
      `}

    ${({ $appearance }) =>
      $appearance === SiteSelectorAppearance.SECONDARY_HEADER &&
      css`
        width: ${toRem(24)} !important;
        height: ${toRem(24)} !important;
        fill: ${theme.color.neutral[900]};
      `}

    ${({ $isOpen }) =>
      $isOpen &&
      css`
        transform: rotate(180deg);
      `}
  }
`;

const ItemWrapper = styled.div<{ $isSelected?: boolean; $appearance: SiteSelectorAppearance }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${toRem(theme.space.xs)};
  width: 100%;

  ${({ $appearance }) =>
    $appearance === SiteSelectorAppearance.DEFAULT &&
    css`
      p {
        color: ${theme.color.neutral[500]};
      }
    `}

  ${H1} {
    word-break: break-word;

    ${({ $isSelected, $appearance }) =>
      $isSelected &&
      $appearance === SiteSelectorAppearance.DEFAULT &&
      css`
        font-weight: ${theme.font.weight.bold} !important;
      `}
  }
`;

const Menu = styled.div<{ $appearance: SiteSelectorAppearance }>`
  position: absolute;
  display: flex;
  flex-direction: column;
  top: 100%;
  left: 0;
  background-color: ${theme.color.white};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  border-radius: ${toRem(4)};
  box-shadow: ${theme.shadow.dialog};
  width: 100%;
  padding: 0 ${toRem(theme.space.xs)};
  z-index: ${theme.zIndex.nav - 1};
  gap: ${toRem(theme.space.xs)};

  ${({ $appearance }) =>
    ($appearance === SiteSelectorAppearance.PAGE_HEADER || $appearance === SiteSelectorAppearance.SECONDARY_HEADER) &&
    css`
      top: calc(100% + ${toRem(8)});
      width: ${toRem(360)};
      padding: ${toRem(theme.space.sm)} ${toRem(theme.space.xs)};

      @media (width < ${theme.breakpoint.small}px) {
        left: 50%;
        transform: translateX(-50%);
        width: calc(100vw - ${toRem(32)});
      }
    `}
`;

/** Owns the scroll, so the search input above it stays pinned and stays outside the `listbox`. */
const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${toRem(theme.space.xs)};
  max-height: ${toRem(242)};
  overflow-y: auto;
`;

const SearchWrapper = styled.div<{ $appearance: SiteSelectorAppearance }>`
  padding: ${toRem(theme.space.sm)} ${toRem(theme.space.xs)};

  ${({ $appearance }) =>
    $appearance === SiteSelectorAppearance.DEFAULT &&
    css`
      border-bottom: ${toRem(1)} solid ${theme.color.neutral[200]};
    `}
`;

const EmptyState = styled.div`
  padding: ${toRem(theme.space.md)};
  text-align: center;

  p {
    color: ${theme.color.neutral[500]};
  }
`;

const MenuItemWrapper = styled.div<{ $appearance: SiteSelectorAppearance }>`
  ${({ $appearance }) =>
    $appearance === SiteSelectorAppearance.DEFAULT &&
    css`
      padding: ${toRem(theme.space.xs)};
      border-bottom: ${toRem(1)} solid ${theme.color.neutral[200]};
    `}
`;

const MenuItem = styled.div<{ $highlighted?: boolean; $appearance: SiteSelectorAppearance }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${toRem(theme.space.md)};
  color: ${theme.color.neutral[900]};
  font-family: ${theme.font.family.euclid};
  font-size: ${toRem(theme.font.size.md)};
  font-weight: ${theme.font.weight.regular};
  line-height: ${lineHeight(theme.font.size.md)};
  border-radius: ${({ $appearance }) => ($appearance === SiteSelectorAppearance.DEFAULT ? toRem(4) : toRem(8))};
  background-color: ${({ $highlighted }) => ($highlighted ? theme.color.neutral[50] : 'transparent')};
  gap: ${toRem(theme.space.xs)};

  ${H1} {
    font-size: ${toRem(theme.font.size.md)};
    line-height: ${lineHeight(theme.font.size.md)};
    font-weight: ${theme.font.weight.regular};
    color: ${theme.color.neutral[900]};
    margin: 0;
    text-align: left;
  }

  &:hover {
    cursor: pointer;
    background-color: ${theme.color.neutral[50]};
  }

  svg {
    position: relative;
    overflow: visible;
  }
`;

interface IOptionProps {
  option?: IEmployerSite;
  index: number;
  setSize: number;
  isSelected: boolean;
  isHighlighted: boolean;
  appearance: SiteSelectorAppearance;
  label: string;
  onSelect: (option?: IEmployerSite) => void;
}

/** Memoised so an arrow key re-renders only the rows whose highlight changed, not the whole list. */
const Option = React.memo<IOptionProps>(({ option, index, setSize, isSelected, isHighlighted, appearance, label, onSelect }) => (
  <MenuItemWrapper $appearance={appearance}>
    <MenuItem id={optionId(index)} role="option" aria-setsize={setSize} aria-posinset={index + 1} aria-selected={isSelected} $highlighted={isHighlighted} $appearance={appearance} onClick={() => onSelect(option)}>
      <ItemWrapper $isSelected={isSelected} $appearance={appearance}>
        <H1 as="span" data-cy="site-option-label">
          {label}
        </H1>
        {option && appearance === SiteSelectorAppearance.DEFAULT && <Text size="xsmall">{buildAddress(option)}</Text>}
      </ItemWrapper>
      {isSelected && <Icon icon="Success" width={16.5} height={16.5} fill={theme.color.secondary[400]} />}
    </MenuItem>
  </MenuItemWrapper>
));

const SiteSelector: React.FC<ISiteSelectorProps> = ({ currentSite, options, onChange, appearance = SiteSelectorAppearance.DEFAULT, allSitesEnabled = false, allSitesLabel = 'All Sites', searchPlaceholder = 'Search Site Name' }) => {
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [query, setQuery] = useState('');
  const siteSelectRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const isSearchable = options.length > SEARCH_THRESHOLD;
  // The query as the filter sees it. `query` itself stays raw — it is the controlled input value.
  const normalizedQuery = query.trim().toLowerCase();
  const activeDescendantId = highlightedIndex >= 0 ? optionId(highlightedIndex) : undefined;

  const closeMenu = useCallback(() => {
    setOpen(false);
    setQuery('');
    setHighlightedIndex(-1);
  }, []);

  const closeAndRefocus = useCallback(() => {
    closeMenu();
    triggerRef.current?.focus();
  }, [closeMenu]);

  useOnClickOutside(siteSelectRef, closeMenu);

  const matches = useMemo(() => {
    if (!normalizedQuery) return options;
    return options.filter((option) => option.name?.toLowerCase().includes(normalizedQuery));
  }, [options, normalizedQuery]);

  // `undefined` is the "All Sites" row: index 0, never filtered out.
  const visibleOptions = useMemo<Array<IEmployerSite | undefined>>(() => (allSitesEnabled ? [undefined, ...matches] : matches), [allSitesEnabled, matches]);

  // Open on the selected row: the scroll effect brings it into view, and arrow keys carry on from there.
  const openMenu = () => {
    setOpen(true);
    setHighlightedIndex(visibleOptions.findIndex((option) => option?.id === currentSite?.id));
  };

  // Stable, so the memoised rows are not invalidated on every render.
  const selectOption = useCallback(
    (option?: IEmployerSite) => {
      onChange(option);
      closeAndRefocus();
    },
    [onChange, closeAndRefocus]
  );

  // Arrow past either end and the highlight wraps round to the other.
  const moveHighlight = (step: number) => {
    const { length } = visibleOptions;
    if (length) setHighlightedIndex((prev) => (prev + step + length) % length);
  };

  // Without the range check, Enter with nothing highlighted would pass undefined, which clears the site.
  const selectHighlighted = () => {
    if (highlightedIndex >= 0 && highlightedIndex < visibleOptions.length) selectOption(visibleOptions[highlightedIndex]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      // Without this the trigger can be reached with Tab but never opened from the keyboard.
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      openMenu();
      return;
    }

    if (!NAV_KEYS.includes(e.key)) return;
    e.preventDefault();

    if (e.key === 'Escape') closeAndRefocus();
    else if (e.key === 'Enter') selectHighlighted();
    else moveHighlight(e.key === 'ArrowDown' ? 1 : -1);
  };

  // Searched inside this selector only: another one on the page would have rows with the same ids.
  const scrollToActive = (block: ScrollLogicalPosition) => {
    if (activeDescendantId) siteSelectRef.current?.querySelector(`#${activeDescendantId}`)?.scrollIntoView({ block });
  };

  // Keep the highlighted row in view as it moves.
  useEffect(() => {
    scrollToActive('nearest');
  }, [activeDescendantId]);

  // On open, centre the selected row instead, so the rows above and below it are visible.
  useEffect(() => {
    if (open) scrollToActive('center');
  }, [open]);

  // Typing can filter away the highlighted row, so clear the highlight whenever the query changes.
  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setHighlightedIndex(-1);
  };

  return (
    <Layout ref={siteSelectRef} onKeyDown={handleKeyDown} $appearance={appearance} data-cy="site-selector">
      <DropDownSelector
        ref={triggerRef}
        onClick={open ? closeMenu : openMenu}
        $isOpen={open}
        $appearance={appearance}
        role="combobox"
        aria-haspopup="listbox"
        aria-controls={LISTBOX_ID}
        aria-expanded={open}
        aria-activedescendant={activeDescendantId}
        tabIndex={0}
      >
        <ItemWrapper $isSelected $appearance={appearance}>
          <H1>{currentSite?.name || allSitesLabel}</H1>
          {appearance === SiteSelectorAppearance.DEFAULT && <Text size="xsmall">{buildAddress(currentSite)}</Text>}
        </ItemWrapper>
        <Icon icon="Dropdown" />
      </DropDownSelector>

      {open && (
        <Menu $appearance={appearance}>
          {isSearchable && (
            <SearchWrapper $appearance={appearance}>
              <InputIconWrapper icon="Search" iconPosition="left">
                <Input
                  autoFocus // mounts with the open menu, so opening lands the cursor here
                  type="text"
                  height="small"
                  value={query}
                  placeholder={searchPlaceholder}
                  aria-label={searchPlaceholder}
                  aria-controls={LISTBOX_ID}
                  aria-activedescendant={activeDescendantId}
                  onChange={handleQueryChange}
                  data-cy="site-selector-search"
                />
              </InputIconWrapper>
            </SearchWrapper>
          )}

          <OptionList role="listbox" id={LISTBOX_ID}>
            {visibleOptions.map((option, index) => (
              // Primitives and stable references only: an inline function or object prop here undoes the memo.
              <Option
                key={option?.id || 'all-sites'}
                option={option}
                index={index}
                setSize={visibleOptions.length}
                isSelected={option?.id === currentSite?.id} // undefined on both sides is the "All Sites" row with nothing chosen
                isHighlighted={index === highlightedIndex}
                appearance={appearance}
                label={option?.name || allSitesLabel}
                onSelect={selectOption}
              />
            ))}

            {normalizedQuery && !matches.length && (
              <EmptyState role="presentation" data-cy="site-selector-no-results">
                <Text size="xsmall">No matches found</Text>
              </EmptyState>
            )}
          </OptionList>
        </Menu>
      )}
    </Layout>
  );
};

export default SiteSelector;
